import type { ReactNode } from 'react'
import { type Control, Controller, type FieldPath, type FieldValues } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { TextField } from '@/components/form'

import { couponErrorText } from './coupon-field-error'

type ControlledNumberFieldProps<T extends FieldValues> = {
  control: Control<T>
  name: FieldPath<T>
  label: string
  helperText?: string
  disabled?: boolean
  min?: number
  max?: number
  step?: number
  'data-tst'?: string
  /** Extra `Field.Description` content rendered inside the field, alongside the
   *  helper/error slot. Base UI registers each message's id and composes them
   *  into the control's `aria-describedby` itself. */
  messageSlot?: ReactNode
}

/** Numeric field bound to a react-hook-form string field. */
export const ControlledNumberField = <T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  disabled,
  min,
  max,
  step,
  'data-tst': dataTst,
  messageSlot,
}: ControlledNumberFieldProps<T>) => {
  const { formatMessage } = useIntl()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          ref={field.ref}
          data-tst={dataTst}
          disabled={disabled}
          error={couponErrorText(formatMessage, fieldState.error)}
          helperText={helperText}
          label={label}
          max={max}
          messageSlot={messageSlot}
          min={min}
          name={field.name}
          step={step}
          type="number"
          value={field.value}
          onBlur={field.onBlur}
          onChange={field.onChange}
        />
      )}
    />
  )
}
